import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { resolveCanonicalAdminUrl } from '@kawai/shared';
import App from './App';
import { AuthProvider } from './lib/auth.js';
import { auth } from './lib/firebase.js';
import {
  consumeImpersonationToken,
  classifyImpersonationFailure,
  type ImpersonationFailureCode,
} from './lib/impersonationConsume.js';
// S-136: v2 の書体（Noto Sans JP）を同梱する。CSS の @import だと Tailwind v4 がインライン化して
// woff2 の URL が解決されないため main.tsx で import する（packages/ui/README.md）。
import '@fontsource-variable/noto-sans-jp';
import './index.css';

// S-07: saas-admin から `#impersonate_token=...` で渡された Custom Token を消費する。
// 副作用: signInWithCustomToken が成功すると AuthProvider の onAuthStateChanged 経由で
// state が impersonation セッションに切り替わる。失敗した場合は console に出して
// 通常の signed-out フローに進む（HomePage が /signin に redirect する）。
async function bootstrap() {
  let impersonationFailure: ImpersonationFailureCode | null = null;
  try {
    const result = await consumeImpersonationToken({
      locationLike: window.location,
      replaceState: window.history.replaceState.bind(window.history),
      auth,
    });
    if (result.kind === 'error') {
      impersonationFailure = classifyImpersonationFailure(result.error);
      // eslint-disable-next-line no-console
      console.error('[impersonation] failed to consume token:', result.error);
    }
  } catch (err) {
    impersonationFailure = 'unknown';
    // eslint-disable-next-line no-console
    console.error('[impersonation] unexpected bootstrap error:', err);
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <AuthProvider impersonationFailure={impersonationFailure}>
        <App />
      </AuthProvider>
    </StrictMode>,
  );
}

// TP-33: 旧ドメイン（apollostation サブドメイン / *.web.app）で開かれた場合は
// 正規ドメイン（tracepit.jp）へ引っ越す（path / query / hash 維持）。旧ブックマークにも
// same-site 化の恩恵を届け、旧オリジンへのセッション・記録の蓄積を止める。
const canonicalUrl = resolveCanonicalAdminUrl(
  'portal',
  window.location.host,
  import.meta.env.MODE,
);
if (canonicalUrl) {
  window.location.replace(
    canonicalUrl.replace(/\/$/, '') +
      window.location.pathname +
      window.location.search +
      window.location.hash,
  );
} else {
  void bootstrap();
}
